/**
 * Utilitário centralizado de logging para o frontend
 * 
 * Logs de DEBUG e INFO só aparecem em modo desenvolvimento.
 * Logs de WARNING e ERROR sempre aparecem.
 */

const isDev = import.meta.env.DEV

export interface Logger {
  debug: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
}

/**
 * Logger padronizado para o projeto
 * 
 * Uso:
 * ```typescript
 * import { logger } from '@/utils/logger'
 * 
 * logger.debug('Mensagem de debug') // Só aparece em desenvolvimento
 * logger.info('Mensagem informativa') // Só aparece em desenvolvimento
 * logger.warn('Aviso') // Sempre aparece
 * logger.error('Erro') // Sempre aparece
 * ```
 */
export const logger: Logger = {
  /**
   * Log de debug - apenas em desenvolvimento
   */
  debug: (...args: unknown[]) => {
    if (isDev) {
      console.log('[DEBUG]', ...args)
    }
  },

  /**
   * Log informativo - apenas em desenvolvimento
   */
  info: (...args: unknown[]) => {
    if (isDev) {
      console.info('[INFO]', ...args)
    }
  },

  /**
   * Log de aviso - sempre aparece
   */
  warn: (...args: unknown[]) => {
    console.warn('[WARN]', ...args)
  },

  /**
   * Log de erro - sempre aparece
   */
  error: (...args: unknown[]) => {
    console.error('[ERROR]', ...args)
  },
}

/**
 * Cria um logger com prefixo customizado
 * 
 * @param prefix Prefixo para todas as mensagens de log
 * @returns Logger com prefixo
 * 
 * Exemplo:
 * ```typescript
 * const moduleLogger = createLogger('AuthContext')
 * moduleLogger.info('Login realizado') // [INFO] [AuthContext] Login realizado
 * ```
 */
export function createLogger(prefix: string): Logger {
  return {
    debug: (...args: unknown[]) => {
      if (isDev) {
        console.log(`[DEBUG] [${prefix}]`, ...args)
      }
    },
    info: (...args: unknown[]) => {
      if (isDev) {
        console.info(`[INFO] [${prefix}]`, ...args)
      }
    },
    warn: (...args: unknown[]) => {
      console.warn(`[WARN] [${prefix}]`, ...args)
    },
    error: (...args: unknown[]) => {
      console.error(`[ERROR] [${prefix}]`, ...args)
    },
  }
}
